import { BMInputField } from "@traveloka/district-bloom";
import IcHotelRoomGuest from "@traveloka/icon-kit-web/react/IcHotelRoomGuest";
import { FC, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useMobileContentResource } from "../../../core/resource/MobileResourceProvider";
import { useTravelerPicker } from "../../../providers/api/TravelerPickerContext";
import { appendTestId, formatMessage } from "../../../utils";
import TravelerPicker from "../common/TravelerPicker/TravelerPicker";
import { Employee } from "../common/TravelerPicker/types";
import { HotelSearchFormValues } from "./types";

const rhfConfig = {
  shouldDirty: true,
  shouldTouch: true,
  shouldValidate: true,
};

type Props = {
  AddEmployeeWidget?: FC<{ addSelectedEmployee: (employee: Employee) => void }>;
  userLoggedIn: {
    name: string;
    email: string;
  };
  permissions: {
    travelerPicker: {
      canBookNonEmployee: boolean;
      canBookForOwn: boolean;
      canBookForOthers: boolean;
      canCreateEmployee: boolean;
    };
  };
  testID?: string;
};

export default function GuestField(props: Props) {
  const {
    permissions: {
      travelerPicker: { canBookForOthers, canBookForOwn },
    },
    userLoggedIn,
    testID,
  } = props;
  const [isVisible, setIsVisible] = useState(false);

  const { CorporateProductSearchForm: cr, CorporateHotelSearchForm: crHl } =
    useMobileContentResource();
  const {
    divisions,
    travelers: employeeList,
    recentPickerDataPassenger,
  } = useTravelerPicker();

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext<HotelSearchFormValues>();
  const [nonEmployee, employees, employeeName] = useWatch({
    control,
    name: ["nonEmployee", "employees", "employeeName"],
  });

  const isDisabled = useMemo(
    () => !canBookForOthers && canBookForOwn,
    [canBookForOthers, canBookForOwn]
  );

  const guestText = useMemo(() => {
    if (
      employees.length === 1 &&
      !nonEmployee.adults &&
      !nonEmployee.childrenAges.length
    ) {
      return employees[0].fullname;
    }

    const parts: string[] = [];

    if (nonEmployee.adults || employees.length) {
      parts.push(
        formatMessage(cr.nonEmployeeAdultUnit, {
          num: nonEmployee.adults + employees.length,
        })
      );
    }

    if (nonEmployee.childrenAges.length > 0) {
      parts.push(
        formatMessage(cr.nonEmployeeChildUnit, {
          num: nonEmployee.childrenAges.length,
        })
      );
    }

    return parts.join(", ");
  }, [nonEmployee, employees, employeeName, cr]);

  const guestErrorMessage =
    errors.employees?.message === "true" ? crHl.guestErrorMessage : "";

  // Set default value to logged in user if employee list available and form is disabled
  // not ideal, because we need to wait employeeList from api
  useEffect(() => {
    if (employeeList.length && !isDisabled) return;
    const user = employeeList.find((emp) => emp.email === userLoggedIn.email);
    setValue("employees", user ? [user] : [], rhfConfig);
  }, [employeeList, userLoggedIn, isDisabled]);

  return (
    <>
      <BMInputField
        editable={false}
        LeadingIcon={IcHotelRoomGuest}
        placeholder={crHl.emptyGuestText}
        label={crHl.travelerLabel}
        value={guestText || ""}
        error={guestErrorMessage}
        disabled={isDisabled}
        variant={isDisabled ? "FIELD" : "DROPDOWN"}
        onDropdownToggle={() => setIsVisible(true)}
        testID={appendTestId(testID, "guest-input")}
      />
      {isVisible && (
        <TravelerPicker
          divisionOptions={divisions}
          employeeList={employeeList}
          onClose={() => setIsVisible(false)}
          onSubmit={(values) => {
            setValue("employees", values.employees, rhfConfig);
            setValue("nonEmployee", values.nonEmployee, rhfConfig);
            setValue(
              "rooms",
              values.employees.length + values.nonEmployee.adults,
              rhfConfig
            );
            setIsVisible(false);
          }}
          recentPickEmployees={recentPickerDataPassenger}
          type="hotel"
          visible
          {...props}
        />
      )}
    </>
  );
}
